import { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import api, { getAssetUrl } from '@/utils/api';
import { Users, Building2, MapPin, Award } from 'lucide-react';

const OurClients = () => {
  const [content, setContent] = useState(null);
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoCount, setLogoCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch both CMS content and client logos in parallel
      const [cmsResponse, logosResponse] = await Promise.all([
        api.get('/cms/pages/our-clients').catch(() => ({ data: null })),
        api.get('/client-logos').catch(() => ({ data: { logos: [], count: 0 } }))
      ]);
      
      setContent(cmsResponse.data);
      setLogos(logosResponse.data.logos || []);
      setLogoCount(logosResponse.data.count || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading {logoCount > 0 ? `${logoCount}` : ''} client logos...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex flex-col">
      <SEO 
        title="Our Clients - VVVSI Job Portal"
        description="Trusted by 32,000+ employers across Tamil Nadu and Puducherry. View our growing network of MNC, Corporate, and Startup partners."
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>
              {content?.title || 'TRUSTED BY 32,000+ EMPLOYERS'}
            </h1>
            <p className="text-lg sm:text-xl text-teal-100 max-w-3xl mx-auto">
              {content?.subtitle || 'We have connected with 21 Sectors of Organizations, Category of MNC, Corporate, India Limited, Private Limited & Startups Across Tamil Nadu and Puducherry UT'}
            </p>
            {content?.hero_description && (
              <p className="text-base text-teal-200 mt-4 max-w-4xl mx-auto">
                {content.hero_description}
              </p>
            )}
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-white py-8 border-b border-slate-100 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="p-4">
                <div className="flex justify-center mb-2">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-teal-600">32,000+</div>
                <div className="text-sm text-slate-600">Employers</div>
              </div>
              <div className="p-4">
                <div className="flex justify-center mb-2">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-teal-600">21</div>
                <div className="text-sm text-slate-600">Industry Sectors</div>
              </div>
              <div className="p-4">
                <div className="flex justify-center mb-2">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-teal-600">2</div>
                <div className="text-sm text-slate-600">States Covered</div>
              </div>
              <div className="p-4">
                <div className="flex justify-center mb-2">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-teal-600">{logoCount || '750+'}</div>
                <div className="text-sm text-slate-600">Partner Companies</div>
              </div>
            </div>
          </div>
        </div>

        {/* Client Logos Grid */}
        <div className="py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-slate-800 mb-2" style={{fontFamily: 'Outfit'}}>
                Our Trusted Partners
              </h2>
              <p className="text-slate-600">
                Showing {logos.length} companies from our network
              </p>
            </div>
            
            <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-3">
              {logos.map((client, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-lg p-2 shadow-sm border border-slate-100 hover:shadow-md hover:border-teal-200 transition-all duration-300 flex items-center justify-center aspect-square group"
                  data-testid={`client-logo-${index}`}
                  title={client.name}
                >
                  <img 
                    src={getAssetUrl(client.logo)}
                    alt={client.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => { 
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ))}
            </div>

            {logos.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500">Loading client logos...</p>
              </div>
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>
              Join Our Growing Network
            </h2>
            <p className="text-slate-300 mb-6">
              Partner with VVVSI to find the best talent for your organization
            </p>
            <a 
              href="/employer/login" 
              className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors"
              data-testid="register-employer-btn"
            >
              Register as Employer
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OurClients;
